import React, {FC, useCallback, useState} from 'react';
import styled, {css} from 'styled-components';

import {buildInteractionIdProps} from '../../../../libs/shared/core/src/helpers/interaction/interactionHelpers';
import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {useTimeout, useWasUnmounted} from '../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {TeammateModel} from '../../../../libs/shared/core/src/models/api/teammateModel';
import {TeamModel} from '../../../../libs/shared/core/src/models/api/teamModel';
import {
  computeIsGlobalNamespace,
  isTeammateNamespace,
} from '../../../../libs/shared/core/src/models/helpers/namespaceHelpers';
import {buildTeammateDisplayName} from '../../../../libs/shared/core/src/models/helpers/teammateModelHelpers';
import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../../libs/shared/core/src/services/tracking/tracking';
import {
  RelativeDateTimeFormatsEnum,
  RelativeDateTimeFormatter,
} from '../../../../libs/shared/ui/src/components/datetime/relativeDateTimeFormatter';
import {TooltipCoordinator} from '../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {textSelection} from '../../../../libs/shared/ui/src/styles/mixins';
import {getCardNoteDisplayDate} from '../../../helpers/models/cardNoteHelpers';
import {CardModel} from '../../../models/api/cardModel';
import {CardNoteModel} from '../../../models/api/cardNoteModel';
import {DomMarkdownRenderer} from '../../common/formatters/markdown/domMarkdownRenderer';
import {markdownDomComponents} from '../../common/formatters/markdown/markdownDomComponents';
import {InlineEditingProps, useInlineEditing} from '../../common/inlineEditing/useInlineEditing';
import {useContactDetailsContext} from '../contactDetailsContext';
import {
  buildContactDetailsHoverPayload,
  buildNoteTrackingPayload,
  ContactDetailsCardNoteActionsEnum,
  contactDetailsHoverOverTimeToReport,
  ContactDetailsHoverSectionsEnum,
} from '../contactDetailsTrackingHelpers';
import {ContactNoteActions} from './contactNoteActions';
import {ComposerNotePartial} from './contactNoteComposer';
import {ContactNoteComposerContainer} from './contactNoteComposerContainer';
import {ContactNoteFooter} from './contactNoteFooter';

/*
 * Constants.
 */

const tooltipDelay = 2000; // 2 seconds

/*
 * Props.
 */

interface ContactNoteProps {
  card: CardModel;
  userNamespace: string;
  className?: string;
  teammate: TeammateModel | undefined;
  team: TeamModel | undefined;
  note: CardNoteModel;
  updateCardNoteAsync: (model: CardNoteModel) => Promise<void>;
  deleteCardNoteAsync: (model: CardNoteModel) => Promise<void>;
}

interface StyledContactNoteContainerDivProps {
  $isReadOnly: boolean;
}

/*
 * Styles.
 */

const StyledContactNoteContainerDiv = styled.div<StyledContactNoteContainerDivProps>`
  background-color: ${(p) => p.theme.palette.yellow.shade10};
  border: 1px solid ${(p) => p.theme.palette.yellow.shade30};
  padding: 12px 16px 14px 16px;
  border-radius: 16px;
  position: relative;

  ${(p) =>
    !p.$isReadOnly &&
    css`
      &:hover {
        background: ${p.theme.palette.yellow.shade20};
      }
    `}
`;

const StyledNoteContentDiv = styled.div`
  font-size: ${fontSizes.medium};
  line-height: 20px;
  font-weight: ${fontWeights.normal};
  color: ${(p) => p.theme.greys.shade90};
  ${textSelection()};
  word-wrap: break-word;

  p {
    margin: 0;
  }

  * + p {
    margin-top: 20px;
  }

  a {
    cursor: pointer;
    color: ${(p) => p.theme.interactiveText};
    font-weight: ${fontWeights.medium};
  }
`;

const StyledContactNoteComposerContainer = styled(ContactNoteComposerContainer)`
  padding: 4px;
  border-radius: 16px;
`;

const StyledTooltipContainerDiv = styled.div`
  display: flex;
  flex-flow: row;
  flex-wrap: wrap;
`;

const StyledNoteVisibilityDiv = styled.div`
  display: inline-block;
  font-weight: ${fontWeights.bold};
`;

const StyledNoteUserSpan = styled.span`
  font-weight: ${fontWeights.normal};
`;

/*
 * Component.
 */

export const ContactNote: FC<ContactNoteProps> = (props) => {
  const {card, className, note, team, teammate, userNamespace, updateCardNoteAsync, deleteCardNoteAsync} =
    props;
  const [shouldShowControls, setShouldShowControls] = useState(false);
  const [isCardTooltipDisabled, setIsCardTooltipDisabled] = useState(false);
  const contactDetailsContext = useContactDetailsContext();
  const [safeSetTimeout] = useTimeout();

  // Watch for unmounting, if we are unmounted clear any timeouts we are tracking
  const {wasUnmountedRef} = useWasUnmounted();

  const onDeleteClick = useCallback(async () => {
    await deleteCardNoteAsync(note);
    trackInterfaceEvent(
      InterfaceEventsEnum.P4_CONTACT_DETAILS_EDIT_NOTE,
      buildNoteTrackingPayload(ContactDetailsCardNoteActionsEnum.DELETE, card, note.namespace),
    );
  }, [note, deleteCardNoteAsync, card]);

  const onSave = async (value: ComposerNotePartial) => {
    // If the notes value is empty, delete the note instead
    if (value.text.trim() === '') {
      onDeleteClick();
      return;
    }
    await updateCardNoteAsync({
      ...note,
      ...value,
    });
    trackInterfaceEvent(
      InterfaceEventsEnum.P4_CONTACT_DETAILS_EDIT_NOTE,
      buildNoteTrackingPayload(ContactDetailsCardNoteActionsEnum.EDIT, card, note.namespace),
    );
  };
  const inlineEditingProps = useInlineEditing<ComposerNotePartial>({
    currentAttributeValue: {
      namespace: note.namespace,
      text: note.text,
    },
    onSave,
    wasUnmounted: wasUnmountedRef.current,
  });

  const onEditClick = () => {
    setShouldShowControls(false);
    setIsCardTooltipDisabled(false);
    inlineEditingProps.onEditStart();
  };

  const onNoteEnter = () => {
    if (card) {
      safeSetTimeout(() => {
        trackInterfaceEvent(
          InterfaceEventsEnum.P4_CONTACT_DETAILS_HOVER,
          buildContactDetailsHoverPayload(card, ContactDetailsHoverSectionsEnum.NOTE),
        );
      }, contactDetailsHoverOverTimeToReport);
    }
  };

  const onNoteExit = () => {
    safeSetTimeout(() => {}, 0);
  };

  const showControls = () => {
    if (contactDetailsContext.isReadOnly) {
      return;
    }
    setShouldShowControls(true);
  };

  const hideControls = () => {
    if (shouldShowControls) {
      setShouldShowControls(false);
    }
  };

  return (
    <>
      {maybeRenderNote(
        className,
        note,
        team,
        teammate,
        inlineEditingProps,
        shouldShowControls,
        isCardTooltipDisabled,
        contactDetailsContext.isReadOnly,
        showControls,
        hideControls,
        setIsCardTooltipDisabled,
        onDeleteClick,
        onEditClick,
        onNoteEnter,
        onNoteExit,
      )}
      {maybeRenderNoteComposer(className, userNamespace, inlineEditingProps)}
    </>
  );
};

const maybeRenderNote = (
  className: string | undefined,
  note: CardNoteModel,
  team: TeamModel | undefined,
  teammate: TeammateModel | undefined,
  inlineEditingProps: InlineEditingProps<ComposerNotePartial>,
  shouldShowControls: boolean,
  isCardTooltipDisabled: boolean,
  isReadOnly: boolean,
  showControls: () => void,
  hideControls: () => void,
  setIsCardTooltipDisabled: React.Dispatch<React.SetStateAction<boolean>>,
  onDeleteClick: () => void,
  onEditClick: () => void,
  onNoteEnter: () => void,
  onNoteExit: () => void,
) => {
  if (inlineEditingProps.isEditing) {
    return null;
  }
  return (
    <TooltipCoordinator
      className={className}
      render={() => (isCardTooltipDisabled ? null : renderNoteTooltip(note, teammate, team))}
      placement="top"
      delay={tooltipDelay}
    >
      <StyledContactNoteContainerDiv
        $isReadOnly={isReadOnly}
        onMouseEnter={() => {
          showControls();
          onNoteEnter();
        }}
        onMouseMove={showControls}
        onMouseLeave={() => {
          hideControls();
          onNoteExit();
        }}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...buildInteractionIdProps(`contact-details-note-${note.id}`)}
      >
        {shouldShowControls && (
          <ContactNoteActions
            noteId={note.id}
            onEditClick={onEditClick}
            onMouseHover={(isHovering: boolean) => {
              setIsCardTooltipDisabled(isHovering);
            }}
            onDeleteClick={onDeleteClick}
          />
        )}
        <StyledNoteContentDiv>
          <DomMarkdownRenderer
            debugIdentifier="contact-note"
            components={markdownDomComponents}
            text={note.text}
          />
        </StyledNoteContentDiv>
        <ContactNoteFooter note={note} team={team} teammate={teammate} />
      </StyledContactNoteContainerDiv>
    </TooltipCoordinator>
  );
};

const maybeRenderNoteComposer = (
  className: string | undefined,
  userNamespace: string,
  inlineEditingProps: InlineEditingProps<ComposerNotePartial>,
) => {
  if (!inlineEditingProps.isEditing) {
    return null;
  }
  return (
    <div className={className}>
      <StyledContactNoteComposerContainer
        value={inlineEditingProps.editedValue}
        onEdit={inlineEditingProps.onEdit}
        onEditSave={inlineEditingProps.onEditSave}
        onEditCancel={inlineEditingProps.onEditCancel}
        error={inlineEditingProps.error}
        isSaving={inlineEditingProps.isSaving}
        userNamespace={userNamespace}
        isEditingExistingNote
      />
    </div>
  );
};

const renderNoteTooltip = (
  note: CardNoteModel,
  teammate: TeammateModel | undefined,
  team: TeamModel | undefined,
) => (
  <StyledTooltipContainerDiv>
    <StyledNoteVisibilityDiv>
      {renderNoteVisibilityExplainer(note, team)}
      <StyledNoteUserSpan>
        &nbsp;<Trans id="contact-notes-tooltip-added-by">Added by</Trans>&nbsp;
        {teammate ? (
          buildTeammateDisplayName(teammate)
        ) : (
          <Trans id="contact-notes-tooltip-added-by-default-teammate">a teammate</Trans>
        )}
        &nbsp;
        <RelativeDateTimeFormatter
          value={getCardNoteDisplayDate(note)}
          format={RelativeDateTimeFormatsEnum.FULL}
        />
        .
      </StyledNoteUserSpan>
    </StyledNoteVisibilityDiv>
  </StyledTooltipContainerDiv>
);

const renderNoteVisibilityExplainer = (note: CardNoteModel, team: TeamModel | undefined) => {
  if (isTeammateNamespace(note.namespace)) {
    return <Trans id="contact-notes-private-note">Only you can view this note.</Trans>;
  }

  if (computeIsGlobalNamespace(note.namespace)) {
    return <Trans id="contact-notes-company-note">The entire company can view this note.</Trans>;
  }

  if (team) {
    return <Trans id="contact-notes-team-note">Only the {team.name} workspace can view this note.</Trans>;
  }

  return null;
};
